import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField,
  Button,
  MenuItem,
  Grid,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  FormControl,
  InputLabel,
  Select,
  Container,
  Divider,
  IconButton,
  Avatar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  LinearProgress,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  CardActions
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Inventory as InventoryIcon,
  Close as CloseIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

import axios from 'axios';


// Custom styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)'
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const PriceCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.success.dark,
}));

const theme = createTheme({
  palette: {
    primary: { 
      main: '#2E3B55',
      light: '#3F517A',
      dark: '#1A2238',
      lighter: 'rgba(46, 59, 85, 0.1)'
    },
    secondary: { 
      main: '#4A6FA5',
      light: '#6B8CBC',
      dark: '#2C4269',
      lighter: 'rgba(74, 111, 165, 0.1)'
    },
    success: {
      main: '#2E7D32',
      light: '#4CAF50',
      dark: '#1B5E20'
    },
    error: {
      main: '#D32F2F',
      light: 'rgba(211, 47, 47, 0.1)',
      lighter: 'rgba(211, 47, 47, 0.05)'
    },
    background: { 
      default: '#F8F9FC',
      paper: '#FFFFFF' 
    },
    text: {
      primary: '#2C3E50',
      secondary: '#5A6A7E'
    }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
      color: '#2C3E50'
    },
    subtitle1: {
      color: '#5A6A7E'
    },
    body1: {
      color: '#2C3E50'
    },
    body2: {
      color: '#5A6A7E'
    }
  },
  components: {
    MuiCard: {
      styleOverrides: { 
        root: { 
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0,0,0,0.05)'
        } 
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { 
          padding: '20px 24px',
          background: 'linear-gradient(to right, rgba(46, 59, 85, 0.03), rgba(46, 59, 85, 0))'
        },
        title: { 
          fontSize: '1.1rem', 
          fontWeight: 600,
          color: '#2C3E50'
        }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F8F9FC',
          '& th': { 
            fontWeight: 600,
            color: '#2C3E50'
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(46, 59, 85, 0.12)'
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          }
        }
      }
    }
  }
});

const SubscriptionPackageForm = () => {
  const [formData, setFormData] = useState({
    packageName: '',
    description: '',
    selectedPackage: '',
    createdBy: 'Admin User'
  });

  const [packageItems, setPackageItems] = useState([]);
  const [subscriptionPackages, setSubscriptionPackages] = useState([]);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [serviceItems, setServiceItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [editingPackage, setEditingPackage] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));


  // Map price list IDs to their categories
  const priceListCategoryMap = useMemo(() => {
    const map = {};
    availablePackages.forEach(pkg => {
      map[pkg.id] = pkg.category || 'N/A';
    });
    return map;
  }, [availablePackages]);

  // Format price to ensure it's a number
  const formatPrice = (price) => {
    const num = Number(price);
    return isNaN(num) ? 0 : num;
  };

  // Format price for display
  const formatPriceDisplay = (price) => {
    return formatPrice(price).toFixed(2);
  };

  // Get item display name with fallback options
  const getItemDisplayName = (item, serviceItems) => {
    if (!item) return 'Unknown Item';
    
    // Try to find the service item
    const serviceItem = serviceItems.find(si => 
      si.id?.toString() === item.item_id?.toString() || 
      si.id?.toString() === item.id?.toString()
    );

    // Return the first non-empty value in order of preference
    return serviceItem?.item_name || 
           item.item_name || 
           item.name || 
           `Item ${item.item_id || item.id}`;
  };

  // Fetch all necessary data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [priceListsResponse, serviceItemsResponse, packagesResponse] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/price-lists`),
          fetch(`${process.env.REACT_APP_API_URL}/jnp_service_items`),
          fetch(`${process.env.REACT_APP_API_URL}/subscription-packages`)
        ]);

        if (!priceListsResponse.ok) throw new Error('Failed to fetch price lists');
        if (!serviceItemsResponse.ok) throw new Error('Failed to fetch service items');
        if (!packagesResponse.ok) throw new Error('Failed to fetch subscription packages');

        const [priceListsData, serviceItemsData, packagesData] = await Promise.all([
          priceListsResponse.json(),
          serviceItemsResponse.json(),
          packagesResponse.json()
        ]);

        // Get full details for each package including items
        const packagesWithItems = await Promise.all(
          packagesData.map(async (pkg) => {
            try {
              const detailResponse = await fetch(`${process.env.REACT_APP_API_URL}/subscription-packages/${pkg.id}`);
              if (detailResponse.ok) {
                const details = await detailResponse.json();
                return {
                  ...details,
                  items: details.items?.map(item => ({
                    ...item,
                    item_name: getItemDisplayName(item, serviceItemsData),
                    item_description: item.item_description || 'No description available'
                  }))
                };
              }
              return pkg;
            } catch (err) {
              console.error(`Error fetching details for package ${pkg.id}:`, err);
              return pkg;
            }
          })
        );

        setAvailablePackages(priceListsData);
        setServiceItems(serviceItemsData);
        setSubscriptionPackages(packagesWithItems);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePackageSelect = async (e) => {
    const packageId = e.target.value;
    if (!packageId) {
      setFormData(prev => ({ ...prev, selectedPackage: '', packageName: '', description: '' }));
      setPackageItems([]);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/price-lists/${packageId}`);
      if (!response.ok) throw new Error('Failed to fetch price list details');
      const priceList = await response.json();

      setFormData(prev => ({
        ...prev,
        selectedPackage: packageId,
        packageName: priceList.name || '',
        description: priceList.description || ''
      }));

      // Fetch service items if not already loaded
      if (!serviceItems.length) {
        const itemsResponse = await fetch(`${process.env.REACT_APP_API_URL}/jnp_service_items`);
        const serviceItemsData = await itemsResponse.json();
        setServiceItems(serviceItemsData);
      }

      const itemsWithDetails = priceList.items?.map(item => {
        // Find matching service item (check both id and item_id for compatibility)
        const serviceItem = serviceItems.find(si =>
          si.id == item.item_id || si.id == item.id
        );

        return {
          ...item,
          qty: 1,
          price: formatPrice(item.price),
          itemId: item.item_id || item.id,
          itemName: serviceItem?.item_name || item.item_name || `Item ${item.item_id || item.id}`,
          itemDescription: serviceItem?.item_description || item.item_description || 'No description available'
        };
      }) || [];

      setPackageItems(itemsWithDetails);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemQtyChange = (index, newQty) => {
    const updatedItems = [...packageItems];
    updatedItems[index] = {
      ...updatedItems[index],
      qty: Math.max(1, newQty)
    };
    setPackageItems(updatedItems);
  };

  const validateForm = () => {
    if (!formData.packageName.trim()) {
      setError('Package name is required');
      return false;
    }
    if (!formData.selectedPackage) {
      setError('Please select a price list');
      return false;
    }
    if (packageItems.length === 0) {
      setError('Please add items to the package');
      return false;
    }
    return true;
  };
  const api = axios.create({
    baseURL: `${process.env.REACT_APP_API_URL}`,
    headers: {
      'Content-Type': 'application/json',
    }
  });

  // Add token automatically from localStorage
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  }, (error) => {
    return Promise.reject(error);
  });


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const totalAmount = parseFloat(calculateTotal(packageItems));

      // Ensure we have fresh service items data
      const serviceItemsResponse = await fetch(`${process.env.REACT_APP_API_URL}/jnp_service_items`);
      const freshServiceItems = await serviceItemsResponse.json();
      console.log('Fresh service items:', freshServiceItems);

      // Process items with service names
      const processedItems = packageItems.map(item => {
        const itemId = item.itemId || item.item_id;
        const serviceItem = freshServiceItems.find(si => String(si.id) === String(itemId));
        console.log(`Processing item ${itemId}:`, { serviceItem, item });
        
        return {
          item_id: itemId,
          item_name: serviceItem?.item_name || item.item_name || `Item ${itemId}`,
          quantity: parseInt(item.qty || item.quantity || 1),
          price: formatPrice(item.price || 0)
        };
      });

      console.log('Processed items:', processedItems);

      const payload = {
        package_name: formData.packageName,
        description: formData.description,
        price_list_id: formData.selectedPackage,
        status: 'active',
        quantity: processedItems.length,
        total: totalAmount,
        items: processedItems
      };
      
      console.log('Submitting payload:', payload);

      const url = editingPackage
        ? `/subscription-packages/${editingPackage.id}`
        : '/subscription-packages';

      const method = editingPackage ? 'put' : 'post';      // ✅ API call with Authorization header automatically handled by interceptor
      const response = await api[method](url, payload);

      // Fetch complete package details for all packages including items
      const packagesResponse = await api.get('/subscription-packages');
      const packagesData = packagesResponse.data;
      
      // Get full details for each package including items
      const packagesWithItems = await Promise.all(
        packagesData.map(async (pkg) => {
          try {
            const detailResponse = await fetch(`${process.env.REACT_APP_API_URL}/subscription-packages/${pkg.id}`);
            if (detailResponse.ok) {
              const details = await detailResponse.json();
              
              // Find service item names for each item
              const itemsWithServiceNames = details.items?.map(item => {
                const serviceItem = serviceItems.find(si => 
                  String(si.id) === String(item.item_id)
                );
                
                console.log('Processing item:', item.item_id, 'Found service item:', serviceItem);
                
                return {
                  ...item,
                  item_name: serviceItem?.item_name || item.item_name || `Item ${item.item_id}`,
                  item_description: serviceItem?.item_description || item.item_description || 'No description available',
                  itemId: item.item_id // Ensure we have itemId for consistency
                };
              }) || [];

              return {
                ...details,
                items: itemsWithServiceNames
              };
            }
            return pkg;
          } catch (err) {
            console.error(`Error fetching details for package ${pkg.id}:`, err);
            return pkg;
          }
        })
      );

      setSubscriptionPackages(packagesWithItems);

      setSuccessMessage(
        editingPackage
          ? 'Package updated successfully!'
          : 'Package created successfully!'
      );
      resetForm();
    } catch (err) {
      console.error('Package error:', err);
      setError(err.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };




  const handleEditPackage = async (pkg) => {
    try {
      setIsLoading(true);

      // 1. Fetch fresh service items data
      const serviceItemsResponse = await fetch(`${process.env.REACT_APP_API_URL}/jnp_service_items`);
      if (!serviceItemsResponse.ok) throw new Error('Failed to fetch service items');
      const currentServiceItems = await serviceItemsResponse.json();
      setServiceItems(currentServiceItems);

      // 2. Fetch the complete package details with items
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription-packages/${pkg.id}`);
      if (!response.ok) throw new Error('Failed to fetch package details');
      const packageDetails = await response.json();

      // 3. Set form data
      setEditingPackage(packageDetails);
      setFormData({
        packageName: packageDetails.package_name,
        description: packageDetails.description || '',
        selectedPackage: packageDetails.price_list_id,
        createdBy: packageDetails.created_by || 'Admin Userssssss'
      });

      // 4. Process items with proper name mapping
      if (packageDetails.items && Array.isArray(packageDetails.items)) {
        const itemsWithDetails = packageDetails.items.map(item => {
          if (!item) return null;

          // Get the correct ID (handling different API response structures)
          const itemId = item.item_id || item.id;

          // Find matching service item (using loose equality to handle string/number IDs)
          const serviceItem = currentServiceItems.find(si =>
            si.id == itemId || si.id == item.item_id
          );

          // Get price safely
          const itemPrice = parseFloat(item.price) || 0;

          return {
            ...item,
            qty: item.quantity || 1,
            price: formatPrice(itemPrice),
            itemId: itemId,
            // Priority: 1. serviceItem name, 2. item.item_name, 3. fallback to ID
            itemName: serviceItem?.item_name || item.item_name || `Item ${itemId}`,
            itemDescription: serviceItem?.item_description || item.item_description || 'No description available'
          };
        }).filter(Boolean); // Remove any null items

        setPackageItems(itemsWithDetails);
      }
    } catch (err) {
      console.error("Edit Package Error:", err);
      setError(err.message || 'Failed to load package for editing');
    } finally {
      setIsLoading(false);
    }
  };














  const handleDeletePackage = async (id) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription-packages/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete package');
      }

      setSubscriptionPackages(prev => prev.filter(pkg => pkg.id !== id));
      setConfirmDelete(null);
      setSuccessMessage('Package deleted successfully!');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      packageName: '',
      description: '',
      selectedPackage: '',
      createdBy: 'Admin User'
    });
    setPackageItems([]);
    setEditingPackage(null);
    setError(null);
  };

  const calculateTotal = (items) => {
    if (!items) return "0.00";
    const total = items.reduce((sum, item) => {
      const price = formatPrice(item.price);
      return sum + (item.qty * price);
    }, 0);
    return total.toFixed(2);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ py: 4, backgroundColor: theme.palette.background.default }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setError(null)}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setSuccessMessage(null)}
          >
            {successMessage}
          </Alert>
        )}

        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: "rgb(25 118 210)"}}>
              <InventoryIcon />
            </Avatar>
          }
          title={editingPackage ? `Edit Package: ${editingPackage.package_name}` : "Create New Subscription Package"}
          subheader="Create and manage subscription packages for your business"
          titleTypographyProps={{ variant: 'h6', fontWeight: 600 }}
          sx={{
            mb: 3,
            p: 3,
            borderRadius: 2,
            backgroundColor: 'white',
            boxShadow: theme.shadows[1]
          }}
          action={
            <Box>
              {editingPackage && (
                <Tooltip title="Cancel editing">
                  <IconButton onClick={resetForm} sx={{ mr: 1 }}>
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Refresh data">
                <IconButton onClick={() => window.location.reload()}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>
          }
        />

        <StyledCard sx={{ mb: 4 }}>
          {isLoading && <LinearProgress />}
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6} style={{ width: "30%" }}>
                  <TextField
                    fullWidth
                    label="Package Name"
                    name="packageName"
                    value={formData.packageName}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="medium"
                    disabled={isLoading}
                  />
                </Grid>

                <Grid item xs={12} md={6} style={{ width: "30%" }}>
                  <FormControl fullWidth size="medium">
                    <InputLabel id="package-select-label">Select Price List</InputLabel>
                    <Select
                      labelId="package-select-label"
                      label="Select Price List"
                      name="selectedPackage"
                      value={formData.selectedPackage}
                      onChange={handlePackageSelect}
                      required
                      disabled={isLoading}
                    >
                      <MenuItem value=""><em>Select a price list</em></MenuItem>
                      {availablePackages.map(pkg => (
                        <MenuItem key={pkg.id} value={pkg.id}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <span>{pkg.name}</span>
                            <Chip
                              label={`${pkg.item_count} items`}
                              size="small"
                              sx={{ ml: 1 }}
                              color={pkg.item_count > 0 ? 'primary' : 'default'}
                            />
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6} style={{ width: "30%" }}>
                  <TextField
                    fullWidth
                    label="Created By"
                    name="createdBy"
                    value={formData.createdBy}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    size="medium"
                    disabled={isLoading}
                  />
                </Grid>

                <Grid item xs={12} style={{ width: "100%" }}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    variant="outlined"
                    size="medium"
                    placeholder="Enter a detailed description of the package..."
                    disabled={isLoading}
                  />
                </Grid>
              </Grid>

              {packageItems.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    Line Items
                    <Chip
                      label={`${packageItems.length} items`}
                      size="small"
                      sx={{ ml: 1, backgroundColor: theme.palette.primary.light, color: 'white' }}
                    />
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Item Name</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell align="right">Unit Price</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isLoading ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <CircularProgress size={24} />
                            </TableCell>
                          </TableRow>
                        ) : packageItems.length > 0 ? (
                          packageItems.map((item, index) => (
                            <StyledTableRow key={index}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {` ${item.item_name}`}
                                  <Tooltip title={item.itemDescription} arrow>
                                    <IconButton size="small" sx={{ ml: 1 }}>
                                      <InfoIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                {/* Debug output: */}
                                <div style={{ fontSize: '10px', color: 'gray' }}>
                                  ID: {item.itemId} | Name: {item.itemName} 
                                </div>
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  value={item.qty}
                                  onChange={(e) => handleItemQtyChange(index, parseInt(e.target.value) || 1)}
                                  inputProps={{ min: 1 }}
                                  size="small"
                                  sx={{ width: 80 }}
                                  disabled={isLoading}
                                />
                              </TableCell>
                              <PriceCell align="right">${formatPriceDisplay(item.price)}</PriceCell>
                              <PriceCell align="right">${(item.qty * formatPrice(item.price)).toFixed(2)}</PriceCell>
                            </StyledTableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No items in this package
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow sx={{ bgcolor: 'rgba(0, 0, 0, 0.04)' }}>
                          <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Grand Total:</TableCell>
                          <PriceCell align="right" sx={{ fontWeight: 'bold' }}>${calculateTotal(packageItems)}</PriceCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={resetForm}
                  disabled={isLoading}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={isLoading}
                  sx={{
                    minWidth: 150,
                    bgcolor: "rgb(25 118 210)",
                  
                  }}
                >
                  {isLoading ? 'Saving...' : editingPackage ? 'Update Package' : 'Save Package'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </StyledCard>

        {subscriptionPackages.length > 0 && (
          <StyledCard>
            <CardHeader
              title="Subscription Packages"
              titleTypographyProps={{
                variant: 'h5',
                fontWeight: 700,
                color: 'text.primary'
              }}
              subheader="Manage your existing subscription packages"
              subheaderTypographyProps={{
                variant: 'body2',
                color: 'text.secondary'
              }}
              action={
                <Box display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showOnlyMine}
                        onChange={(e) => setShowOnlyMine(e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label="My Packages"
                    labelPlacement="start"
                    sx={{ mr: 2 }}
                  />
                  <Chip
                    label={`${subscriptionPackages.length} packages`}
                    color="primary"
                    variant="outlined"
                    size="medium"
                    sx={{
                      fontWeight: 600,
                      borderWidth: 2,
                      '& .MuiChip-label': {
                        px: 1.5
                      }
                    }}
                  />
                </Box>
              }
              sx={{
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            />
            <CardContent>
              <Grid container spacing={3}>
                {subscriptionPackages
                  .filter(pkg => !showOnlyMine || pkg.created_by === user?.name)
                  .map(pkg => (
                    <Grid item xs={12} md={6} lg={4} key={pkg.id}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          borderRadius: '12px',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 24px rgba(46, 59, 85, 0.15)',
                            borderColor: theme.palette.primary.light
                          }
                        }}
                      >
                        <CardHeader
                          avatar={
                            <Avatar
                              sx={{
                                bgcolor: theme.palette.primary.lighter,
                                color: theme.palette.primary.dark
                              }}
                            >
                              {pkg.package_name?.charAt(0) || 'P'}
                            </Avatar>
                          }
                          title={
                            <Typography
                              variant="subtitle1"
                              fontWeight={600}
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {pkg.package_name}
                            </Typography>
                          }
                          subheader={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'flex', alignItems: 'center' }}
                            >
                              <CheckCircleIcon
                                fontSize="inherit"
                                color="success"
                                sx={{ mr: 0.5 }}
                              />
                              Active • Created by {pkg.created_by || 'Admin'}
                            </Typography>
                          }
                          action={
                            <Box display="flex">
                              <Tooltip title="Edit package">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditPackage(pkg)}
                                  sx={{ color: theme.palette.primary.main }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete package">
                                <IconButton
                                  size="small"
                                  onClick={() => setConfirmDelete(pkg.id)}
                                  sx={{ color: theme.palette.error.main }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          }
                          sx={{
                            pb: 1,
                            '& .MuiCardHeader-content': {
                              overflow: 'hidden'
                            }
                          }}
                        />

                        <CardContent sx={{ flex: 1, py: 0 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mb: 2,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {pkg.description || 'No description provided'}
                          </Typography>

                          <Box sx={{
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: 1,
                            p: 1.5,
                            mb: 2,
                            border: '1px solid',
                            borderColor: 'divider'
                          }}>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="caption" fontWeight={500}>
                                Price List ID
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {pkg.price_list_id || 'N/A'}
                              </Typography>
                            </Box>

                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="caption" fontWeight={500}>
                                Category
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {/* Show category name from price list lookup */}
                                {priceListCategoryMap && priceListCategoryMap[pkg.price_list_id] ? priceListCategoryMap[pkg.price_list_id] : 'N/A'}
                              </Typography>
                            </Box>



                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="caption" fontWeight={500}>
                                Items
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {pkg.items?.length || 0}
                              </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                              <Typography variant="caption" fontWeight={500}>
                                Created
                              </Typography>
                              <Typography variant="caption" fontWeight={600}>
                                {new Date(pkg.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{
                            backgroundColor: theme.palette.primary.lighter,
                            borderRadius: 1,
                            p: 1.5,
                            textAlign: 'center'
                          }}>
                            <Typography variant="caption" display="block" color="text.secondary">
                              Total Value
                            </Typography>
                            <Typography
                              variant="h6"
                              fontWeight={700}
                              color={theme.palette.primary.dark}
                            >
                              ${calculateTotal(pkg.items?.map(item => ({
                                price: item.price,
                                qty: item.quantity
                              })) || [])}
                            </Typography>
                          </Box>
                        </CardContent>

                        <CardActions sx={{ p: 2, pt: 0 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            startIcon={<InventoryIcon />}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              py: 1,
                              borderRadius: 1,
                              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                              '&:hover': {
                                boxShadow: `0 4px 12px ${theme.palette.primary.main}40`
                              }
                            }}
                          >
                            View Package Details
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  ))}
              </Grid>
            </CardContent>
          </StyledCard>
        )}
        <Dialog
          open={Boolean(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this package? This action cannot be undone.</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setConfirmDelete(null)}
              startIcon={<CloseIcon />}
              variant="outlined"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeletePackage(confirmDelete)}
              color="error"
              startIcon={<DeleteIcon />}
              disabled={isLoading}
              variant="contained"
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default SubscriptionPackageForm;